import React from 'react';

import { AutoCompletePrediction } from '@opendoor/cinderblocks/core/AddressInput';
import { EventTrackingAction, globalObservability } from '@opendoor/observability/slim';
import {
  OdProtosCasaData_Result,
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { updateAnswers } from '../components/api';
import { startAddressVerification } from '../components/api/addressInputHelpers';
import { IAddressInputAddress } from '../components/api/addressTypes';
import { CONSUMER_FE_URL, COSMOS_URL } from '../components/globals';
import { setCookie } from '../components/shared/trackers';
import { useObservability } from './observability';
import useQueryParams from './queryParams';

const Sentry = globalObservability.getSentryClient();

export type SelectedAddress = IAddressInputAddress & {
  id?: string;
  result?: OdProtosCasaData_Result[];
  source?: 'google' | 'casa';
};

export const ADDRESS_INVALID_RESULT: OdProtosCasaData_Result[] = [
  'ADDRESS_NIL',
  'ADDRESS_UNKNOWN',
  'ADDRESS_MISSING_PREMISE',
  'ADDRESS_INVALID_PREMISE',
  'ADDRESS_NOT_SUPPORTED',
  'ADDRESS_NOT_REAL',
  'ADDRESS_INVALID',
  'ADDRESS_INVALID_STREET',
  'ADDRESS_INVALID_SUB_PREMISE',
  'ADDRESS_MISSING_BOX_NUMBER',
  'ADDRESS_INVALID_BOX_NUMBER',
  'ADDRESS_EXTRANEOUS_INPUT',
];

interface OnboardingFlowInput {
  address: SelectedAddress;
  product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  trackEvent: ReturnType<typeof useObservability>['trackEvent'];
  trackingTaxonomy?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  trackingKeywords?: string;
  inputLocation?: string;
  onSuccess?: (address: IAddressInputAddress, token: string, pdpUrl?: string) => void;
  partnerReferralId?: string;
  pageSetPath?: string;
  addressUUID?: string;
  canRedirect?: boolean;
  relationshipAnswers?: Record<string, string>;
}

export const handleAddressVerificationSuccess = ({
  address,
  token,
  trackEvent,
  pdpUrl,
  pageSetPath,
  onSuccess,
  relationshipAnswers,
}: {
  address: SelectedAddress;
  token: string;
  trackEvent: ReturnType<typeof useObservability>['trackEvent'];
  pdpUrl?: string;
  pageSetPath?: string;
  onSuccess?: (address: IAddressInputAddress, token: string, pdpUrl?: string) => void;
  relationshipAnswers?: Record<string, string>;
}) => {
  const performRedirection = () => {
    if (pdpUrl) {
      trackEvent('inputting_address_success' as EventTrackingAction, 'pdp-redirect');
      if (onSuccess) {
        return onSuccess(address, token, pdpUrl);
      }
      window.location.href = `${COSMOS_URL}${pdpUrl}?utm_source=seller&utm_medium=web&utm_campaign=ae_pdp`;
    } else {
      trackEvent('inputting_address_success' as EventTrackingAction, 'onboarding-redirect');
      if (onSuccess) {
        return onSuccess(address, token, pdpUrl);
      }
      const redirectUrl = pageSetPath
        ? `${CONSUMER_FE_URL}/seller-lead/${token}${pageSetPath}`
        : `${CONSUMER_FE_URL}/seller-lead/${token}`;
      window.location.href = redirectUrl;
    }
  };
  const answers: Record<string, unknown> = {
    'home.user_verified_address': true,
    ...relationshipAnswers,
  };
  if (relationshipAnswers?.['seller.relation_to_owner']) {
    answers['experimental'] = [
      {
        question_key: 'seller.relation_to_owner_from_cosmos',
        string_values: {
          values: [relationshipAnswers['seller.relation_to_owner']],
        },
      },
    ];
  }
  updateAnswers(answers, '', token)
    .then(() => {
      performRedirection();
    })
    .catch((err) => {
      Sentry.captureException?.(err);
    });
};

export const gateProceedToOnboardingFlow = async ({
  address,
  product,
  trackEvent,
  trackingTaxonomy,
  channel,
  trackingKeywords,
  inputLocation,
  onSuccess,
  partnerReferralId,
  pageSetPath,
  addressUUID,
  canRedirect = true,
  relationshipAnswers,
}: OnboardingFlowInput) => {
  if (trackingTaxonomy) {
    setCookie('sourceForTracking', trackingTaxonomy, 1);
  }

  const error = await startAddressVerification({
    addressUUID,
    address,
    product,
    channel,
    trackingKeywords,
    inputLocation,
    onSuccess: (token, pdpUrl, pageSetPath) => {
      handleAddressVerificationSuccess({
        address,
        token,
        trackEvent,
        pdpUrl,
        pageSetPath,
        onSuccess,
        relationshipAnswers,
      });
    },
    partnerReferralId,
    pageSetPath,
  });

  if (error) {
    if (error.type === 'invalid-address') {
      if (canRedirect) {
        window.location.href = '/manual-address-entry';
      } else {
        throw new Error(error.message);
      }
    } else {
      window.location.href = '/error';
    }
  }
};

type AddressValidationContextProps = {
  validatedAddress?: SelectedAddress;
  setValidatedAddress: React.Dispatch<React.SetStateAction<SelectedAddress | undefined>>;
  addressResults: OdProtosCasaData_Result[];
  setAddressResults: React.Dispatch<React.SetStateAction<OdProtosCasaData_Result[]>>;
  isPredictionSelectLoading: boolean;
  setIsPredictionSelectLoading: React.Dispatch<React.SetStateAction<boolean>>;
  firstPrediction: AutoCompletePrediction | null;
  setFirstPrediction: React.Dispatch<React.SetStateAction<AutoCompletePrediction | null>>;
  partnerReferralId?: string;
  setPartnerReferralId: React.Dispatch<React.SetStateAction<string | undefined>>;
  pageSetPath?: string;
  setPageSetPath: React.Dispatch<React.SetStateAction<string | undefined>>;
  trackingKeywords?: string;
  setTrackingKeywords: React.Dispatch<React.SetStateAction<string | undefined>>;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  setChannel: React.Dispatch<
    React.SetStateAction<OdProtosSellReceptionData_SellerInput_Channel | undefined>
  >;
};

const AddressValidationContext = React.createContext<AddressValidationContextProps>({
  validatedAddress: undefined,
  setValidatedAddress: () => null,
  addressResults: [],
  setAddressResults: () => null,
  isPredictionSelectLoading: false,
  setIsPredictionSelectLoading: () => null,
  firstPrediction: null,
  setFirstPrediction: () => null,
  partnerReferralId: undefined,
  setPartnerReferralId: () => null,
  pageSetPath: undefined,
  setPageSetPath: () => null,
  trackingKeywords: undefined,
  setTrackingKeywords: () => null,
  channel: undefined,
  setChannel: () => null,
});

export const useAddressValidationContext = () => React.useContext(AddressValidationContext);

export const AddressValidationContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    address,
    result,
    pageSetPath: pageSetPathQueryParam,
    trackingKeyword: trackingKeyworksQueryParam,
    partnerReferralId: partnerReferralIdQueryParam,
    channel: channelQueryParam,
  } = useQueryParams();
  const [validatedAddress, setValidatedAddress] = React.useState<SelectedAddress | undefined>(
    () => {
      if (!address) return undefined;
      return JSON.parse(address as string);
    },
  );

  const [addressResults, setAddressResults] = React.useState<OdProtosCasaData_Result[]>(() => {
    if (!result) return [];
    if (Array.isArray(result)) {
      return result as OdProtosCasaData_Result[];
    }
    return [result as OdProtosCasaData_Result];
  });
  const [isPredictionSelectLoading, setIsPredictionSelectLoading] = React.useState(false);
  const [firstPrediction, setFirstPrediction] = React.useState<AutoCompletePrediction | null>(null);
  const [partnerReferralId, setPartnerReferralId] = React.useState<string | undefined>(() => {
    if (!partnerReferralIdQueryParam) return undefined;
    return partnerReferralIdQueryParam as string;
  });
  const [pageSetPath, setPageSetPath] = React.useState<string | undefined>(() => {
    if (!pageSetPathQueryParam) return undefined;
    return pageSetPathQueryParam as string;
  });
  const [trackingKeywords, setTrackingKeywords] = React.useState<string | undefined>(() => {
    if (!trackingKeyworksQueryParam) return undefined;
    return trackingKeyworksQueryParam as string;
  });
  const [channel, setChannel] = React.useState<
    OdProtosSellReceptionData_SellerInput_Channel | undefined
  >(() => {
    if (!channelQueryParam) return 'DEFAULT';
    return channelQueryParam as OdProtosSellReceptionData_SellerInput_Channel;
  });
  return (
    <AddressValidationContext.Provider
      value={{
        trackingKeywords,
        setTrackingKeywords,
        pageSetPath,
        setPageSetPath,
        validatedAddress,
        setValidatedAddress,
        addressResults,
        setAddressResults,
        isPredictionSelectLoading,
        setIsPredictionSelectLoading,
        firstPrediction,
        setFirstPrediction,
        partnerReferralId,
        setPartnerReferralId,
        channel,
        setChannel,
      }}
    >
      {children}
    </AddressValidationContext.Provider>
  );
};
